import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Divider } from "components/design/Divider";
import { Button } from "components/DesignSystem/Button/Button";
import { KeyDownWrapper } from "components/DesignSystem/KeydownWrapper/KeydownWrapper";
import { Paperclip } from "components/icons/Chat/Paperclip";
import Pencil from "components/icons/pencil";
import { LinkInkleBillModal } from "components/LinkInkleBillModal/LinkInkleBillModal";
import { LinkInkleInvoiceModal } from "components/LinkInkleInvoiceModal/LinkInkleInvoiceModal";
import { ChangeCategory } from "components/Transaction/UnLinkTransaction";
import { colors } from "constants/colors";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import AddClipIcon from "static/images/AddClipIcon.svg";
import { Transactions as TransactionsType } from "types/Models/books";
import { Invoices } from "./Slider/Invoice";
import { LinkedBills } from "./Slider/LinkedBills";
import { LinkedInvoices } from "./Slider/LinkedInvoices";
import { InfoItem } from "./Slider/TransactionSlider";
import {
  useGetMappedBillQuery,
  useGetMappedInvoiceQuery,
} from "store/apis/transactions";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { LockedUi } from "components/LockedUi/LockedUi";

export const AddTransactionInvoice = ({
  transactionDetails,
  zIndexClass = "t-z-popover",
  hasWritePermission,
}: {
  transactionDetails: TransactionsType;
  zIndexClass?: string;
  hasWritePermission: boolean;
}) => {
  const transactionId = transactionDetails.transaction.uuid;
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const { isMobile } = useWindowSize();
  const linkInkleInvoiceModal = useModal();
  const linkInkleBillModal = useModal();
  const changeCategoryModal = useModal();
  const [changeCategoryTarget, setChangeCategoryTarget] = useState<
    "invoice" | "bill"
  >("invoice");

  const entityId = useCurrentEntityId();

  const openLinkInvoiceModal = () => {
    setPopoverOpen(false);
    linkInkleInvoiceModal.open();
  };

  const openLinkBillModal = () => {
    setPopoverOpen(false);
    linkInkleBillModal.open();
  };

  const handleConfirmChangeCategory = () => {
    changeCategoryModal.close();
    if (changeCategoryTarget === "bill") {
      linkInkleBillModal.open();
    } else {
      linkInkleInvoiceModal.open();
    }
  };

  const handleLinkClick = (target: "invoice" | "bill") => {
    const shouldShowWarning =
      categoryName === "Bank Transfer" || categoryName === "Pay Down Credit";

    if (shouldShowWarning) {
      setChangeCategoryTarget(target);
      changeCategoryModal.open();
    } else if (target === "bill") {
      openLinkBillModal();
    } else {
      openLinkInvoiceModal();
    }
  };

  const {
    data: linkedInvoices,
    isLoading: isLinkedTxnDataLoading,
    isSuccess: isLinkedTxnDataSuccess,
  } = useGetMappedInvoiceQuery(
    { entityId, transactionId },
    {
      skip:
        !entityId || !transactionId || !transactionDetails.has_linked_invoices,
    }
  );

  const {
    data: linkedBills,
    isLoading: isLinkedBillsLoading,
    isSuccess: isLinkedBillsSuccess,
  } = useGetMappedBillQuery(
    { entityId, transactionId },
    {
      skip: !entityId || !transactionId || !transactionDetails.has_linked_bills,
    }
  );

  const linkedInvoiceItems = (linkedInvoices?.invoices || []).filter(
    (inv) => inv.financial_record_type !== FINANCIAL_RECORD_TYPE.BILL
  );
  const linkedBillItems = (linkedBills?.invoices || []).filter(
    (inv) => inv.financial_record_type === FINANCIAL_RECORD_TYPE.BILL
  );

  const isCreditCard = transactionDetails?.transaction?.is_credit_card;
  const isCredit =
    transactionDetails?.transaction?.amount &&
    transactionDetails?.transaction?.amount > 0;
  const isDebit =
    transactionDetails?.transaction?.amount &&
    transactionDetails?.transaction?.amount < 0;

  const categoryName = transactionDetails?.transaction?.category?.name ?? "";

  const isSplitTransaction = Boolean(
    transactionDetails?.split_transactions &&
      transactionDetails.split_transactions.length > 0
  );

  const hasLinkedInvoice = transactionDetails?.invoices.some(
    ({ has_inkle_invoice }) => has_inkle_invoice
  );
  const hasLinkedBill = transactionDetails?.invoices.some(
    ({ has_inkle_bill }) => has_inkle_bill
  );
  const hasInvoices = transactionDetails.invoices.length > 0;

  const uploadTooltip = isDebit ? "Add Bill" : "Add Invoice";

  const triggerIcon =
    !hasWritePermission && hasInvoices ? (
      <ConditionalToolTip condition={false}>
        <Button
          customType="ghost"
          size="small"
          onClick={() => setPopoverOpen(true)}
        >
          <div className="t-flex t-items-center t-text-text-30 t-text-caption t-gap-0.5">
            <Paperclip size="16" />
            {transactionDetails.invoices.length}
          </div>
        </Button>
      </ConditionalToolTip>
    ) : (
      <ConditionalToolTip condition={uploadTooltip}>
        <Button
          customType="ghost"
          size="small"
          onClick={() => setPopoverOpen(true)}
        >
          <img src={AddClipIcon} alt="AddClipIcon" />
        </Button>
      </ConditionalToolTip>
    );

  const isAlwaysVisible = hasInvoices || hasLinkedBill;

  return (
    <div
      className={classNames(
        "hover:t-bg-surface t-flex t-justify-center t-items-center t-rounded",
        {
          "t-opacity-100": isAlwaysVisible,
          "t-opacity-0 group-hover:t-opacity-100": !isAlwaysVisible,
        }
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <Popover.Root open={isPopoverOpen} onOpenChange={setPopoverOpen}>
        <Popover.Trigger asChild>
          <span>{triggerIcon}</span>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={classNames(
              zIndexClass,
              "t-w-[390px] t-max-h-[480px] t-overflow-y-auto t-shadow-light-60 t-bg-surface t-rounded-lg t-p-6 t-pt-5 sm:t-w-80 sm:t-p-4"
            )}
            side={isMobile ? "bottom" : "left"}
            alignOffset={-10}
            align="start"
            sideOffset={4}
            collisionPadding={16}
            onClick={(e) => e.stopPropagation()}
            onInteractOutside={(e) => {
              // @ts-ignore
              if (e.target?.getAttribute("data-popover-trigger") === "true") {
                e.preventDefault();
              }
            }}
          >
            <div className="t-space-y-4">
              <Invoices
                transactionId={transactionId}
                isBillTransaction={Boolean(isDebit)}
              />

              <LockedUi hasAccess={hasWritePermission}>
                {isCredit && !isCreditCard && !hasLinkedInvoice && (
                  <div className="t-flex t-justify-between t-py-1 t-px-3 t-items-center t-border t-border-solid t-rounded t-border-purple-10 t-bg-surface-purple">
                    <div className="t-text-body-sm">
                      Link transaction to Inkle invoices
                    </div>
                    <ConditionalToolTip
                      condition={
                        isSplitTransaction &&
                        "Cannot link with Inkle Invoice since it is a split transaction"
                      }
                    >
                      <div>
                        <Button
                          size="small"
                          onClick={() => handleLinkClick("invoice")}
                          disabled={isSplitTransaction}
                        >
                          Link
                        </Button>
                      </div>
                    </ConditionalToolTip>
                  </div>
                )}
              </LockedUi>

              <LockedUi hasAccess={hasWritePermission}>
                {isDebit && !hasLinkedBill && (
                  <div className="t-flex t-justify-between t-py-1 t-px-3 t-items-center t-border t-border-solid t-rounded t-border-purple-10 t-bg-surface-purple">
                    <div className="t-text-body-sm">
                      Link transaction to Inkle bills
                    </div>
                    <ConditionalToolTip
                      condition={
                        isSplitTransaction &&
                        "Cannot link with Inkle Bill since it is a split transaction"
                      }
                    >
                      <div>
                        <Button
                          size="small"
                          onClick={() => handleLinkClick("bill")}
                          disabled={isSplitTransaction}
                        >
                          Link
                        </Button>
                      </div>
                    </ConditionalToolTip>
                  </div>
                )}
              </LockedUi>

              <Async.Root
                isSuccess={isLinkedTxnDataSuccess}
                isLoading={isLinkedTxnDataLoading}
                isEmpty={linkedInvoiceItems.length === 0}
              >
                <Async.Empty>
                  <></>
                </Async.Empty>
                <Async.Success>
                  {linkedInvoiceItems.length > 0 && hasLinkedInvoice && (
                    <>
                      <Divider />
                      <KeyDownWrapper>
                        <InfoItem
                          label={
                            <div className="t-flex t-justify-between t-items-center">
                              <span>Linked Inkle Invoice</span>
                              <LockedUi hasAccess={hasWritePermission}>
                                <Button
                                  customType="icon"
                                  size="small"
                                  onClick={openLinkInvoiceModal}
                                >
                                  <Pencil
                                    color={colors.neutral.DEFAULT}
                                    size="16"
                                  />
                                </Button>
                              </LockedUi>
                            </div>
                          }
                        >
                          <LinkedInvoices
                            transactionId={transactionId}
                            invoices={linkedInvoiceItems}
                          />
                        </InfoItem>
                      </KeyDownWrapper>
                    </>
                  )}
                </Async.Success>
              </Async.Root>

              <Async.Root
                isSuccess={isLinkedBillsSuccess}
                isLoading={isLinkedBillsLoading}
                isEmpty={linkedBillItems.length === 0}
              >
                <Async.Empty>
                  <></>
                </Async.Empty>
                <Async.Success>
                  {linkedBillItems.length > 0 && hasLinkedBill && (
                    <>
                      <Divider />
                      <KeyDownWrapper>
                        <InfoItem
                          label={
                            <div className="t-flex t-justify-between t-items-center">
                              <span>Linked Inkle Bill</span>
                              <LockedUi hasAccess={hasWritePermission}>
                                <Button
                                  customType="icon"
                                  size="small"
                                  onClick={openLinkBillModal}
                                >
                                  <Pencil
                                    color={colors.neutral.DEFAULT}
                                    size="16"
                                  />
                                </Button>
                              </LockedUi>
                            </div>
                          }
                        >
                          <LinkedBills
                            transactionId={transactionId}
                            invoices={linkedBillItems}
                          />
                        </InfoItem>
                      </KeyDownWrapper>
                    </>
                  )}
                </Async.Success>
              </Async.Root>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {linkInkleInvoiceModal.isOpen && (
        <LinkInkleInvoiceModal
          transactionId={transactionId}
          isOpen={linkInkleInvoiceModal.isOpen}
          close={linkInkleInvoiceModal.close}
        />
      )}

      {linkInkleBillModal.isOpen && (
        <LinkInkleBillModal
          transactionId={transactionId}
          isOpen={linkInkleBillModal.isOpen}
          close={linkInkleBillModal.close}
        />
      )}

      <ChangeCategory
        isOpen={changeCategoryModal.isOpen}
        close={changeCategoryModal.close}
        onConfirm={handleConfirmChangeCategory}
        categoryName={categoryName}
      />
    </div>
  );
};
